/**
 * 货币转换工具
 * 使用 FXRatesAPI 获取实时汇率，将各种货币转换为人民币 (CNY)
 */

// 货币符号到货币代码的映射
const CURRENCY_SYMBOL_MAP: Record<string, string> = {
    '¥': 'CNY',
    '￥': 'CNY',
    '$': 'USD',
    '€': 'EUR',
    '£': 'GBP',
    '₽': 'RUB',
    '₣': 'CHF',
    '₹': 'INR',
    '₫': 'VND',
    '฿': 'THB',
    '₩': 'KRW',
    'A$': 'AUD',
    'C$': 'CAD',
    'HK$': 'HKD',
    'NT$': 'TWD',
    'S$': 'SGD',
    'R$': 'BRL',
    '₺': 'TRY',
    '₴': 'UAH',
    '₱': 'PHP',
    'RM': 'MYR',
    'kr': 'SEK', // 瑞典克朗，也可能是丹麦/挪威克朗
    'zł': 'PLN',
    'Kč': 'CZK',
    'Ft': 'HUF',
};

// 离线备用汇率表（基于 CNY，大概值）
const FALLBACK_RATES_TO_CNY: Record<string, number> = {
    CNY: 1,
    USD: 7.25,
    EUR: 7.90,
    GBP: 9.20,
    JPY: 0.048,
    RUB: 0.075,
    CHF: 8.20,
    INR: 0.086,
    VND: 0.00029,
    THB: 0.21,
    KRW: 0.0053,
    AUD: 4.70,
    CAD: 5.30,
    HKD: 0.93,
    TWD: 0.22,
    SGD: 5.35,
    BRL: 1.25,
    TRY: 0.21,
    UAH: 0.17,
    PHP: 0.125,
    MYR: 1.62,
    SEK: 0.67,
    PLN: 1.80,
    CZK: 0.31,
    HUF: 0.019,
};

// 汇率缓存
interface ExchangeRateCache {
    rates: Record<string, number>;
    timestamp: number;
}

let rateCache: ExchangeRateCache | null = null;
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 小时

/**
 * 将货币符号转换为货币代码
 */
export function getCurrencyCode(symbol: string): string {
    const trimmed = symbol.trim();
    // 先尝试精确匹配
    if (CURRENCY_SYMBOL_MAP[trimmed]) {
        return CURRENCY_SYMBOL_MAP[trimmed];
    }
    // 尝试匹配首字符
    if (CURRENCY_SYMBOL_MAP[trimmed[0]]) {
        return CURRENCY_SYMBOL_MAP[trimmed[0]];
    }
    // 如果是3字母代码，直接返回
    if (/^[A-Z]{3}$/.test(trimmed.toUpperCase())) {
        return trimmed.toUpperCase();
    }
    // 默认返回 CNY
    return 'CNY';
}

/**
 * 从 FXRatesAPI 获取汇率
 */
async function fetchExchangeRates(): Promise<Record<string, number>> {
    try {
        // FXRatesAPI 免费接口，以 CNY 为基准
        const response = await fetch('https://api.fxratesapi.com/latest?base=CNY');

        if (!response.ok) {
            throw new Error(`API request failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data.rates) {
            throw new Error('Invalid API response');
        }

        // API 返回的是 1 CNY = X 外币，我们需要反转为 1 外币 = X CNY
        const ratesToCNY: Record<string, number> = { CNY: 1 };
        for (const [currency, rate] of Object.entries(data.rates)) {
            if (typeof rate === 'number' && rate > 0) {
                ratesToCNY[currency] = 1 / rate;
            }
        }

        return ratesToCNY;
    } catch (error) {
        console.warn('Failed to fetch exchange rates, using fallback rates:', error);
        return FALLBACK_RATES_TO_CNY;
    }
}

/**
 * 获取汇率（带缓存）
 */
export async function getExchangeRates(): Promise<Record<string, number>> {
    const now = Date.now();

    // 检查缓存是否有效
    if (rateCache && (now - rateCache.timestamp) < CACHE_DURATION_MS) {
        return rateCache.rates;
    }

    // 获取新的汇率
    const rates = await fetchExchangeRates();

    // 更新缓存
    rateCache = {
        rates,
        timestamp: now,
    };

    return rates;
}

/**
 * 获取离线汇率（同步方法）
 */
export function getFallbackRates(): Record<string, number> {
    return { ...FALLBACK_RATES_TO_CNY };
}

/**
 * 将指定货币金额转换为人民币
 * @param amount 金额
 * @param currencySymbol 货币符号（如 €、$ 等）或货币代码（如 EUR、USD 等）
 * @param rates 汇率表
 * @returns 人民币金额
 */
export function convertToCNY(
    amount: number,
    currencySymbol: string,
    rates: Record<string, number>
): number {
    if (amount <= 0) return 0;

    const currencyCode = getCurrencyCode(currencySymbol);
    const rate = rates[currencyCode] ?? 1;

    return amount * rate;
}

/**
 * 计算节点的月消费（人民币）
 * @param price 价格
 * @param billingCycle 计费周期（天）
 * @param currency 货币符号
 * @param rates 汇率表
 * @returns 月消费（人民币）
 */
export function calculateMonthlyCostCNY(
    price: number,
    billingCycle: number,
    currency: string,
    rates: Record<string, number>
): number {
    // 跳过免费或无效数据
    if (price <= 0 || price === -1) return 0;
    if (billingCycle <= 0 || billingCycle === -1) return 0;

    // 计算日均费用 * 30 天
    const dailyCost = price / billingCycle;
    const monthlyCost = dailyCost * 30;

    return convertToCNY(monthlyCost, currency, rates);
}

/**
 * 计算节点的年消费（人民币）
 * @param price 价格
 * @param billingCycle 计费周期（天）
 * @param currency 货币符号
 * @param rates 汇率表
 * @returns 年消费（人民币）
 */
export function calculateYearlyCostCNY(
    price: number,
    billingCycle: number,
    currency: string,
    rates: Record<string, number>
): number {
    // 跳过免费或无效数据
    if (price <= 0 || price === -1) return 0;
    if (billingCycle <= 0 || billingCycle === -1) return 0;

    // 计算日均费用 * 365 天
    const dailyCost = price / billingCycle;
    const yearlyCost = dailyCost * 365;

    return convertToCNY(yearlyCost, currency, rates);
}

/**
 * 格式化人民币金额
 * @param amount 金额
 * @returns 格式化后的字符串
 */
export function formatCNY(amount: number): string {
    if (amount >= 10000) {
        return `¥${(amount / 10000).toFixed(2)}万`;
    }
    if (amount >= 1000) {
        return `¥${amount.toFixed(0)}`;
    }
    return `¥${amount.toFixed(2)}`;
}
